import { AlertCircle, FileSignature } from 'lucide-react'
import { Spinner } from '../../lib/shadcn/spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import { Chip } from '../interviews/Chip'
import { formatDate } from '../../utils/interviews'
import { type RolloutRow, chipInfo } from '../../utils/contractRollout'
import { RolloutAction } from './RolloutAction'

function StatusChip({ row }: { row: RolloutRow }) {
  const info = chipInfo(row)
  return (
    <Chip tone={info.tone} {...(info.className ? { className: info.className } : {})}>
      {info.label}
    </Chip>
  )
}

export function RolloutTable({
  rows,
  loading,
  error,
  canSend,
  busyId,
  sentIds,
  onSend,
  onResend,
}: {
  rows: RolloutRow[]
  loading: boolean
  error: string | null
  canSend: boolean
  busyId: string | null
  sentIds: Set<string>
  onSend: (row: RolloutRow) => void
  onResend: (row: RolloutRow) => void
}) {
  if (error) {
    return (
      <div className="m-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error}</span>
      </div>
    )
  }

  if (loading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Loading rollout...
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-sm text-muted-foreground">
        <FileSignature className="h-8 w-8 opacity-40" />
        No active drivers match your filters.
      </div>
    )
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Driver</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead>Signed</TableHead>
              <TableHead>Start of service</TableHead>
              {canSend ? <TableHead className="text-right">Action</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.driver_id}>
                <TableCell className="font-medium text-foreground">
                  {r.driver_name || 'Unnamed driver'}
                </TableCell>
                <TableCell className="max-w-xs">
                  <span className="block truncate text-muted-foreground">
                    {r.primary_email || '—'}
                  </span>
                </TableCell>
                <TableCell>
                  <StatusChip row={r} />
                </TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {r.contract_agreement_version ?? '—'}
                  {r.offered_addendum ? <span className="block text-xs">Addendum No. 1 route</span> : null}
                </TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {formatDate(r.sent_at)}
                </TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {formatDate(r.signed_at)}
                </TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {formatDate(r.service_since)}
                </TableCell>
                {canSend ? (
                  <TableCell className="text-right">
                    <div className="flex justify-end">
                      <RolloutAction
                        row={r}
                        canSend={canSend}
                        busy={busyId === r.driver_id}
                        justSent={sentIds.has(r.driver_id)}
                        onSend={onSend}
                        onResend={onResend}
                      />
                    </div>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile stacked cards */}
      <div className="space-y-3 p-3 lg:hidden">
        {rows.map((r) => (
          <div
            key={r.driver_id}
            className="rounded-lg border border-border bg-card p-4"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  {r.driver_name || 'Unnamed driver'}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {r.primary_email || '—'}
                </p>
              </div>
              <StatusChip row={r} />
            </div>
            <dl className="mt-3 grid grid-cols-4 gap-2 text-xs">
              <div>
                <dt className="text-muted-foreground">Version</dt>
                <dd className="font-medium text-foreground">{r.contract_agreement_version ?? '—'}{r.offered_addendum ? ', addendum' : ''}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Sent</dt>
                <dd className="font-medium text-foreground">{formatDate(r.sent_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Signed</dt>
                <dd className="font-medium text-foreground">{formatDate(r.signed_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Start of service</dt>
                <dd className="font-medium text-foreground">{formatDate(r.service_since)}</dd>
              </div>
            </dl>
            {canSend ? (
              <div className="mt-3">
                <RolloutAction
                  row={r}
                  canSend={canSend}
                  busy={busyId === r.driver_id}
                  justSent={sentIds.has(r.driver_id)}
                  onSend={onSend}
                  onResend={onResend}
                />
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </>
  )
}
